import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FormattedAIResponse from "./FormattedAIResponse";
import DistributionIcon from '../../../../assets/images/icons/distribution.svg';
import MixEngineerIcon from '../../../../assets/images/icons/mixing-icon.svg'
import MasteringEngineerIcon from '../../../../assets/images/icons/mastering-icon.svg'
import MixEnhancerIcon from '../../../../assets/images/icons/mix-enhancer-icon.svg'
import MixAnalystIcon from '../../../../assets/images/icons/mix-analyst-icon.svg'
import { generateChatHistoryRanges } from "../../../../helper/dataFormat";
import { useDispatch, useSelector } from "react-redux";
import { getChatHistory } from "../../../../redux/actions/amplitudeAIActions";
import { setStep, updateFormData } from "../../../../redux/actions";
import { DistributionModal } from "../../../../components/DistributionAccess/DistributionModal";

const BUTTON_DEFINITIONS = [
  // Campaign buttons — each with specific keyword weights
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-youtube",
    color: "from-red-600 to-gray-600",
    payload: { objective: "YouTube Growth", callToAction: "Subscribe", platformType: "YouTube", shouldShowFeatureFM: false },
    keywords: { youtube: 10, 'grow youtube': 8, subscribers: 6, views: 4 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-instagram",
    color: "from-red-600 to-white-600",
    payload: { objective: "Instagram Growth", callToAction: "Follow", platformType: "Instagram", shouldShowFeatureFM: false },
    keywords: { instagram: 10, reels: 6, followers: 4, 'instagram ads': 10, 'ig growth': 8 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-spotify",
    color: "from-green-600 to-white-600",
    payload: { objective: "Spotify Growth", callToAction: "Play Now", platformType: "", shouldShowFeatureFM: false },
    keywords: { spotify: 10, streams: 6, listeners: 5, 'play on spotify': 10, 'spotify ads': 10, 'spotify playlist': 8, 'spotify growth': 10 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-facebook",
    color: "from-green-600 to-white-600",
    payload: { objective: "Promote an Event", callToAction: "Follow Page", platformType: "Facebook", shouldShowFeatureFM: false },
    keywords: { facebook: 10, meta: 8, fb: 8,'facebook ads': 10 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-music-note-list",
    color: "from-green-600 to-orange-600",
    payload: { objective: "Playlist Growth", callToAction: "Listen Now", type: "playlist", shouldShowFeatureFM: true },
    keywords: { playlist: 10, 'grow playlist': 10, 'playlist growth': 10, 'add to playlist': 9 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-music-note-beamed",
    color: "from-green-600 to-blue-600",
    payload: { objective: "Playlist Growth", callToAction: "Play Now", type: "track", shouldShowFeatureFM: true },
    keywords: { 'promote track': 10, 'new track': 8, 'single': 7, 'release track': 9 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-box",
    color: "from-green-600 to-blue-600",
    payload: { objective: "Sell Your Merch", callToAction: "Shop Now", platformType: "", shouldShowFeatureFM: false },
    keywords: { merch: 10, merchandise: 10, 'sell merch': 10, 'merch store': 10 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-calendar-event",
    color: "from-pink-600 to-blue-600",
    payload: { objective: "Promote an Event", callToAction: "Book Now", platformType: "instagram", shouldShowFeatureFM: false },
    keywords: { 'promote event': 10, 'event campaign': 10, concert: 8, tour: 7, show: 5, gig: 8 }
  },
  {
    label: "Launch Campaign",
    route: "/admin/growth-engine/campaign",
    icon: "bi-link",
    color: "from-blue-600 to-orange-600",
    payload: { objective: "Promote Custom Link", callToAction: "Open Link", platformType: "instagram", shouldShowFeatureFM: false },
    keywords: { 'custom link': 10, 'promote link': 10, 'link campaign': 10, 'bio link': 8 }
  },
  // Analytics
  {
    label: "Analyse Your Data",
    route: "/admin/data-analyst",
    icon: "bi-graph-up",
    color: "from-blue-600 to-cyan-600",
    keywords: { analytics: 10, 'my data': 10, statistics: 8, stats: 8, insights: 7, performance: 6, growth: 5, metrics: 8 }
  },
  {
    label: "View Geographic Insights",
    route: "/admin/data-analyst/catalog",
    icon: "bi-geo-alt",
    color: "from-red-600 to-emerald-600",
    keywords: { 'geographic': 10, location: 9, countries: 8, regions: 8, cities: 8, 'where are my fans': 10 }
  },
  {
    label: "View Milestones",
    route: "/admin/data-analyst/milestones",
    icon: "bi-music-note-list",
    color: "from-blue-600 to-emerald-600",
    keywords: { milestone: 10, milestones: 10, activity: 8, achievements: 7 }
  },
  {
    label: "View Radio Plays",
    route: "/admin/data-analyst/radioplay",
    icon: "bi-broadcast",
    color: "from-yellow-600 to-blue-600",
    keywords: { radio: 10, 'radio play': 10, 'radio airplay': 10, airplay: 9 }
  },
  // Strategy / Planning
  {
    label: "Plan Your Strategy",
    route: "/admin/artist-manager/release-and-financial-planner",
    icon: "bi-calendar-check",
    color: "from-green-600 to-emerald-600",
    keywords: { strategy: 10, plan: 8, roadmap: 9, 'artist strategy': 10, profile: 6, 'career plan': 10 }
  },
  {
    label: "Launch Task Planner",
    route: "/admin/artist-manager/release-and-financial-planner",
    icon: "bi-list-task",
    color: "from-orange-600 to-cyan-600",
    keywords: { task: 10, tasks: 10, 'task planner': 10, todo: 8, 'to do': 8, checklist: 8 }
  },
  {
    label: "Launch Financial Planner",
    route: "/admin/artist-manager/release-and-financial-planner",
    icon: "bi-cash-stack",
    color: "from-blue-600 to-gray-600",
    keywords: { finance: 10, financial: 10, budget: 10, revenue: 9, income: 9, earnings: 8, royalties: 8 }
  },
  // Content & Links
  // {
  //   label: "Create Content",
  //   route: "/admin/artist-manager/link-and-fan-hub",
  //   icon: "bi-palette",
  //   color: "from-orange-600 to-red-600",
  //   keywords: { 'fans': 10, 'fan hub': 10, 'social content': 9 }
  // },
  {
    label: "Create Music Link",
    route: "/admin/artist-manager/build-music-link",
    icon: "bi-file-music",
    color: "from-pink-600 to-blue-600",
    keywords: { 'music link': 10, 'smart link': 10, 'create music link': 10 }
  },
  {
    label: "Create Presave Link",
    route: "/admin/artist-manager/create-pre-save",
    icon: "bi-people",
    color: "from-orange-600 to-blue-600",
    keywords: {'landing page': 8, 'pre-save': 10, presave: 10, 'create presave': 10 }
  },
  {
    label: "Create Tour Link",
    route: "/admin/artist-manager/build-tour-link",
    icon: "bi-backpack",
    color: "from-orange-600 to-emerald-600",
    keywords: { tour: 10, 'tour link': 10, 'tour planner': 10, 'tour plan': 10, 'tour dates': 10, touring: 8 }
  },
  // Audio
  {
    label: "Launch Mastering Engineer",
    route: "/admin/urstudio/audio-engineer",
    svgIcon: MasteringEngineerIcon,
    color: "from-pink-600 to-purple-600",
    keywords: { 
      master: 9, 
      mastering: 10, 
      'mastering my music': 10, 
      'master music': 10,
      'mastering music': 10,
      'urstudio': 10, 
      'sound master': 10,
      'master my sound': 10,
      'master my track': 10,
      'master my audio': 10,
      'master my music': 10,
      'audio master': 10, 
      'music master': 10,
      'track master': 10,
      'master track': 10,
      'master audio': 10,
      'master release': 10, 
      'release master': 10,
      'audio mastering': 10, 
      'mastering engineer': 10, 
      'audio engineer': 10 
    }
  },
  {
    label: "Launch Mixing Engineer",
    route: "/admin/urstudio/mixing-engineer",
    svgIcon: MixEngineerIcon,
    color: "from-pink-600 to-purple-600",
    keywords: { 
      'mixing engineer': 10, 
      'mix multiple tracks': 10, 
      'amplify music': 8,
      'mix my music': 10,
      'mix my audio': 10,
      'mix my track': 10,
      'mixing my music': 10,
      'mixing my audio': 10,
      'sound mix': 10,
      'mixing sound': 10,
      'mix my sound': 10,
      'sound mixing': 10,
      'track mix': 10,
      'urstudio': 9, 
      'mixed track': 10,
      'vocal mix': 10,
      'mix vocal': 10,
      'audio clean': 10,
      'clean audio': 10,
      'bass mix': 10,
      'mix audio': 10, 
      'mix track': 10, 
      'mix music': 10,
      'music mix': 10,
      'mixing music': 10,
      'mixing audio': 10,
      'audio mixing': 10,
      'mix': 10,
    }
  },
  {
    label: "Launch Mix Enhancer",
    route: "/admin/urstudio/mix-enhancer",
    svgIcon: MixEnhancerIcon,
    color: "from-pink-600 to-purple-600",
    keywords: { 
      'enhance mix': 10, 
      'enhance audio': 10,
      'audio enhance': 10,
      'audio enhancement': 10,
      'track enhancement': 10,
      'mix enhancement': 10,
      'music quality': 10,
      'sound quality': 10,
      'sound enhancement': 10,
      'enhance sound': 10,
      'enhance my sound quality': 10,
      'enhance sound quality': 10,
      'enhance loudness': 10,
      'enhance tonal profile': 10,
      'loudness enhancement': 10,
      'fix loudness': 10,
      'fix audio loudness': 10,
      'fix track loudness': 10,
      'fix music loudness': 10,
      'fix mix loudness': 10,
      'fix stereo width': 10,
      'fix audio stereo width': 10,
      'fix track stereo width': 10,
      'fix music stereo width': 10,
      'fix mix stereo width': 10,
      'fix tonal profile': 10,
      'fix track sound': 10,
      'fix tonal balance': 10,
      'fix music issues': 10,
      'fix music quality': 10,
      'fix audio quality': 10,
      'fix clipping': 10,
      'fix Dynamic Range Compression': 10,
      'stem fixing': 10,
      'fix stems': 10,
      'mix stems': 10,
      'stems split': 10,
      'split stems': 10,
      'enhancement quality': 10,
      'enhancement mix': 10,
      'enhance my track': 10,
      'enhance my music': 10,
      'enhance my audio': 10,
      'enhance this track': 10,
      'enhance this audio': 10,
      'enhance this mix': 10,
      'enhance this music': 10,
      'mix enhance': 10, 
      'sonic enhance': 10, 
      'enhancement strategy': 10,
      'music enhancement': 10,
      'urstudio': 8, 
      'enhance audio': 10, 
      'audio enhance': 10, 
      'enhance track': 10 
    }
  },
  {
    label: "Launch Mix Analyst",
    route: "/admin/urstudio/mix-analyst",
    svgIcon: MixAnalystIcon,
    color: "from-pink-600 to-purple-600",
    keywords: { 
      'mix report': 10, 
      'track mix report': 10,
      'audio mix report': 10,
      'generate mix report': 10,
      'generate audio mix report': 10,
      'generate music mix report': 10,
      'generate music report': 10,
      'generate track report': 10,
      'generate track mix report': 10,
      'music analysis': 10,
      'music quality analysis': 10,
      'analyze sound quality':10,
      'mix analyst': 8, 
      'mix analysis': 10, 
      'urstudio': 7, 
      'sonic analysis': 10, 
      'analyze mix': 10, 
      'analyze audio': 10, 
      'analyze your track': 8,
      'analyze your audio': 8,
      'analyze your music': 8,
      'analyze my track': 8,
      'analyze my music': 8,
      'analyze my audio': 8,
      'analyze sound quality': 8,
      'analyze your current audio': 8,
      'analyze your current track': 8, 
      'track analysis': 10 
    }
  },
  // Distribution
  {
    label: "Launch Distribution",
    showDistributionModal: true,
    svgIcon: DistributionIcon,
    color: "bg-theme_clr4",
    keywords: { distribution: 10, 'distribution strategy': 8, release: 8, invite: 10, 'request invite': 10, distribute: 10, 'release distribution': 10, 'release strategy': 6, 'digital distribution': 10, 'distribute track': 8, 'track distributn': 9, strategy: 7, plan: 7, roadmap: 8, 'artist strategy': 8 }
  },
  // Fan Base
  {
    label: "View Fan Base",
    route: "/admin/artist-manager/link-and-fan-hub#fanbase",
    icon: "bi-people-fill",
    color: "from-orange-600 to-emerald-600",
    keywords: { fanbase: 10, 'fan base': 10, fans: 7, audience: 7, followers: 6 }
  },
];

const scoreButton = (buttonDef, combinedText) => {
  let score = 0;
  for (const [phrase, weight] of Object.entries(buttonDef.keywords)) {
    if (combinedText.includes(phrase)) {
      score += weight;
    }
  }
  return score;
};

export const getCTAButtons = (userMsg, aiMsg) => {
  const userText = (userMsg || "").toLowerCase();
  const aiText  = (aiMsg  || "").toLowerCase();
  const combinedText = `${userText} ${aiText}`;

  // Score every button definition
  const scored = BUTTON_DEFINITIONS
    .map(def => ({ def, score: scoreButton(def, combinedText) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score);

  const seen = new Set();
  const buttons = [];

  for (const { def } of scored) {
    if (buttons.length >= 5) break;
    if (!seen.has(def.label)) {
      seen.add(def.label);
      const { keywords, ...buttonProps } = def;
      buttons.push(buttonProps);
    }
  }

  if (buttons.length === 0 && combinedText.length > 20) {
    const hasActionWords = ['help', 'how', 'what', 'show', 'get', 'need', 'want', 'should', 'can', '?']
      .some(w => combinedText.includes(w));

    if (hasActionWords) {
      buttons.push({
        label: "Explore Solutions",
        route: "/admin/amplitude-ai",
        icon: "bi-lightbulb",
        color: "from-yellow-600 to-orange-600"
      });
    }
  }

  return buttons;
};

//CTAButtons component
const CTAButtons = ({ userMsg, aiMsg }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const buttons = getCTAButtons(userMsg, aiMsg);

  if (buttons.length === 0) return null;

  return (
    <div className="flex gap-2 mt-3 flex-wrap">
      {buttons.map((button, idx) => (
        <button
          key={idx}
          className="flex flex-col items-center"
          tabIndex={-1}
          onClick={() => {
            if (button.route) {
              navigate(button.route)
            }
            if (button.showDistributionModal) {
              setShowModal(!showModal)
            }
            if (button.payload) {
              dispatch(updateFormData(button.payload))
              dispatch(setStep(1))
            }
          }}
        >
          <span className={`flex items-center justify-center bg-gradient-to-r ${button.color} hover:shadow-lg text-white font-medium text-sm md:text-base px-3 md:px-5 py-2 md:py-3 rounded-md border border-white transition-all duration-200 shadow-md hover:scale-105`}>
            {button.label}
            {button.icon ?
              <i className={`bi ${button.icon} text-lg ml-2`}></i>
              : button.svgIcon ?
                <img
                  src={button.svgIcon}
                  alt="distribution"
                  style={{ filter: "brightness(0) invert(1)" }}
                  className="h-[20px] w-[20px] min-w-[20px] ml-2"
                />
                : ''
            }

          </span>
        </button>
      ))}
      <DistributionModal isOpen={showModal} onClose={() => setShowModal(false)}/>
    </div>
  );
};

function ChatHistory({ messages, isLoading }) {
  const dispatch = useDispatch()
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [load, setLoad] = useState(false)

  const [selectedDate, setSelectedDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem('chatHistorySelectedDate');
      return stored || "today";
    } catch (error) {
      console.error('Failed to load selected date:', error);
      return "today";
    }
  });

  const [selectedRange, setSelectedRange] = useState(() => {
    try {
      const stored = sessionStorage.getItem('chatHistorySelectedRange');
      return stored || "Today";
    } catch (error) {
      console.error('Failed to load selected range:', error);
      return "Today";
    }
  });

  const artistDetails = useSelector(state => state.artistDetails);
  const { artistInfo } = artistDetails;
  const reducerData = useSelector(state => state?.amplitudeReducer);
  const historyData = reducerData?.history?.data?.length > 0 ? reducerData?.history?.data : []
  const chatContainerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const storedDate = sessionStorage.getItem('chatHistorySelectedDate');
        const storedRange = sessionStorage.getItem('chatHistorySelectedRange');

        if (storedDate && storedDate !== selectedDate) {
          setSelectedDate(storedDate);
        }
        if (storedRange && storedRange !== selectedRange) {
          setSelectedRange(storedRange);
        }
      } catch (error) {
        console.error('Failed to read storage:', error);
      }
    };

    handleStorageChange();
    const interval = setInterval(handleStorageChange, 100);

    return () => clearInterval(interval);
  }, [selectedDate, selectedRange]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [historyData, messages, isLoading, selectedDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const ranges = generateChatHistoryRanges()

  const handleSelect = useCallback((label, value) => {
    setSelectedRange(label);
    setSelectedDate(value);

    try {
      sessionStorage.setItem('chatHistorySelectedDate', value);
      sessionStorage.setItem('chatHistorySelectedRange', label);
    } catch (error) {
      console.error('Failed to save selected date/range:', error);
    }

    dispatch({ type: 'CLEAR_CHAT_MESSAGES' });

    dispatch(getChatHistory(value, setLoad))
    setDropdownOpen(false);
  }, [dispatch])

  useEffect(() => {
    dispatch(getChatHistory(selectedDate, setLoad))
  }, [])

  const allMessages = [
    ...historyData.slice().sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    ...messages.filter(msg => msg.sender === "user" || msg.sender === "ai")
  ];

  // Chat Skeleton Loader Component
  const ChatSkeleton = () => (
    <div className="flex flex-col gap-6">
      {[1, 2, 3].map((item) => (
        <div key={item} className="flex flex-col gap-4">
          {/* User Message Skeleton */}
          <div className="flex md:flex-row flex-col gap-2 mb-4" style={{ alignItems: "end" }}>
            <div className="flex-1 flex justify-end">
              <div className="bg-gray-200 animate-pulse rounded-lg px-6 py-5 shadow-sm md:w-[40%] w-[100%] h-16"></div>
            </div>
          </div>

          {/* AI Response Skeleton */}
          <div className="flex md:flex-row flex-col gap-2 mb-4" style={{ alignItems: "end" }}>
            <div className="flex-1">
              <div className="bg-gray-100 animate-pulse rounded-lg px-6 py-5 shadow-sm h-32"></div>
            </div>
            <div className="flex justify-end desktop-justify-start md:w-[40%] w-[80%]">
              <div className="bg-gray-100 animate-pulse rounded-lg h-10 w-full"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Empty State Component
  const EmptyState = () => (
    <div className="flex flex-1 items-center justify-center mt-[-52px]">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <svg
            className="mx-auto h-24 w-24 text-[#fa6138] opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-theme_clr2 mb-3">
          Hello {artistInfo.name}, Start Your Journey with Amplitude AI
        </h3>
        <p className="text-[#fa6138] text-lg mb-2">
          Your AI-powered music assistant is ready to help you grow your career.
        </p>
        <p className="text-[#fa6138] text-base">
          Ask about your streams, revenue, promotion strategies, or get personalized insights!
        </p>
      </div>
    </div>
  );

  return (
    <div
      ref={chatContainerRef}
      className="chat-container overflow-y-auto flex flex-col gap-4 bg-theme_clr1 rounded-xl p-4 sm:p-5"
      style={{
        minHeight: '67vh',
        maxHeight: 'calc(100vh - 300px)'
      }}
    >
      {/* Dropdown Chat History button */}
      <div className="sticky top-0 z-10 rounded-t-xl">
        <div className="relative inline-block text-left" ref={dropdownRef}>
          {/* Trigger Button */}
          <button
            data-tour="chat-history-dropdown"
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="inline-flex justify-between items-center w-42 px-4 py-2 text-sm font-semibold text-white bg-[#fa6138] rounded-md shadow hover:bg-[#e5552f]"
          >
            {selectedRange}
            <svg
              className={`ml-2 h-4 w-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {dropdownOpen && (
            <div className="absolute left-0 mt-2 w-42 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
              <div className="py-1">
                {ranges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => handleSelect(range.label, range.value)}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedRange === range.label
                      ? 'bg-[#fa6138] text-white font-semibold'
                      : 'text-[#0A0A4A] hover:bg-[#fa6138] hover:text-white'
                      }`}
                  >
                    {range.label}
                    {selectedRange === range.label && (
                      <svg
                        className="inline-block ml-2 h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {load ? (
        <ChatSkeleton />
      ) : allMessages.length === 0 && !isLoading ? (
        <EmptyState />
      ) : (
        <>
          {allMessages.map((msg, idx) => {
            const userMessage = msg.userMessage || (msg.sender === "user" ? msg.text : "");
            const aiMessage = msg.aiResponse || (msg.sender === "ai" ? msg.text : "");

            return (
              <div key={msg._id || idx}>
                {userMessage && (
                  <div
                    className="flex md:flex-row flex-col gap-2 mb-3"
                    style={{ alignItems: "end" }}
                  >
                    <div className="flex-1 flex justify-end">
                      <div className="bg-[#fa6138] text-white font-semibold rounded-lg px-4 sm:px-6 py-4 sm:py-5 shadow-sm text-sm sm:text-base text-center md:w-[40%] w-[100%]">
                        {userMessage}
                      </div>
                    </div>
                  </div>
                )}

                {aiMessage && (
                  <div
                    className="flex md:flex-row flex-col gap-2 mb-3"
                    style={{ alignItems: "end" }}
                  >
                    <div className="flex-1">
                      <div className="bg-white text-[#fa6138] font-semibold rounded-lg px-4 sm:px-6 py-4 sm:py-5 shadow-sm text-sm sm:text-base">
                        <FormattedAIResponse rawText={aiMessage} />
                      </div>
                    </div>
                    <div className="flex justify-end desktop-justify-start md:w-[40%] w-[80%]">
                      <CTAButtons userMsg={userMessage} aiMsg={aiMessage} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Loading indicator for new messages */}
          {isLoading && (
            <div className="flex md:flex-row flex-col gap-2" style={{ alignItems: "start" }}>
              <div className="flex-1">
                <div className="bg-white text-[#fa6138] font-semibold rounded-lg px-4 sm:px-6 py-4 sm:py-5 shadow-sm text-sm sm:text-base">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#fa6138] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 bg-[#fa6138] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 bg-[#fa6138] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    <span className="ml-2 text-xs sm:text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
              <div className="md:w-[40%] w-[80%]"></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChatHistory;